import React, { useEffect, useRef, useState } from 'react';
import { camerasAPI } from '../../utils/api';
import './RoiSelector.css';

const roiIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/roi.svg`;
const roiBtnIcon = {
  save: `${process.env.PUBLIC_URL}/icons/svg_icon/save.svg`,
  clear: `${process.env.PUBLIC_URL}/icons/svg_icon/clear.svg`,
  sbros: `${process.env.PUBLIC_URL}/icons/svg_icon/sbros.svg`,
  cancel: `${process.env.PUBLIC_URL}/icons/svg_icon/cancel.svg`,
} as const;

interface RoiSelectorProps {
  camera: {
    source_id: string | null;
    hls_out?: string | null;
    webrtc_out?: string | null;
    status?: any;
  };
  cameraModels: string[];
  onClose: () => void;
  onUpdated?: () => void;
}

// Локальная функция для отображения названий моделей по-русски
const getModelDisplayName = (modelName: string): string => {
  const key = (modelName || '').trim().toLowerCase();
  const modelTranslations: Record<string, string> = {
    multiple_rtsp: 'Машины и люди',
    license_plate_recognition: 'Распознавание гос. номеров',
    lpd: 'Номерной знак',
    people: 'Люди',
    person: 'Люди',
    car: 'Машины',
    vehicle: 'Машины',
    bus: 'Автобусы',
    truck: 'Грузовики',
    police: 'Полиция',
    ambulance: 'Скорая помощь',
    firetruck: 'Пожарная машина',
    smoking: 'Курение',
    smoking_detector: 'Курение',
    garbage_truck_detector: 'Мусоровоз',
    garbage_truck: 'Мусоровоз',
    tractor_detector: 'Трактор',
    tractor: 'Трактор',
    fire_and_smoke_detector: 'Огонь и дым',
    fire: 'Огонь',
    smoke: 'Дым',
    emergency_car_detector: 'Спецтранспорт',
  };

  return modelTranslations[key] || modelName;
};

interface RoiState {
  isDrawing: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  rect: { x: number; y: number; w: number; h: number } | null;
}

const RoiSelector: React.FC<RoiSelectorProps> = ({ camera, cameraModels, onClose, onUpdated }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>(cameraModels[0] || '');
  const [roiState, setRoiState] = useState<RoiState>({
    isDrawing: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    rect: null,
  });
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'loading' | null; message: string }>({ type: null, message: '' });
  const [videoWidth, setVideoWidth] = useState<number>(1920);
  const [videoHeight, setVideoHeight] = useState<number>(1080);
  const [minConfidence, setMinConfidence] = useState<string>('0.75'); // 0.0–1.0 (стартовое значение)
  const [cooldown, setCooldown] = useState<string>('900'); // секунды (стартовое значение)

  // Получаем размеры видео из status
  useEffect(() => {
    if (camera.status) {
      const statusData = camera.status;
      if (statusData.resolution?.width && statusData.resolution?.height) {
        setVideoWidth(statusData.resolution.width);
        setVideoHeight(statusData.resolution.height);
      } else if (statusData.src_width && statusData.src_height) {
        setVideoWidth(statusData.src_width);
        setVideoHeight(statusData.src_height);
      } else if (statusData.width && statusData.height) {
        setVideoWidth(statusData.width);
        setVideoHeight(statusData.height);
      }
    }
  }, [camera.status]);

  // Подтягиваем текущие настройки точности и задержки из статуса камеры (если есть)
  useEffect(() => {
    if (!camera.status || !camera.status.models || !selectedModel) return;

    const modelStatus = camera.status.models[selectedModel];
    if (!modelStatus) return;

    // Ищем значение confidence (поддерживаем несколько возможных ключей на всякий случай)
    const currentConfidence =
      typeof modelStatus.min_confidence === 'number'
        ? modelStatus.min_confidence
        : typeof modelStatus.confidence_threshold === 'number'
        ? modelStatus.confidence_threshold
        : undefined;

    if (currentConfidence !== undefined) {
      setMinConfidence(currentConfidence.toString());
    }

    // Ищем значение задержки (cooldown / debounce)
    const currentCooldown =
      typeof modelStatus.cooldown === 'number'
        ? modelStatus.cooldown
        : typeof modelStatus.debounce_sec === 'number'
        ? modelStatus.debounce_sec
        : undefined;

    if (currentCooldown !== undefined) {
      setCooldown(currentCooldown.toString());
    }
  }, [camera.status, selectedModel]);

  // Вспомогательная функция: преобразование координат видео → координаты canvas
  const convertVideoToCanvasCoords = (
    videoX: number,
    videoY: number,
    videoW: number,
    videoH: number,
    canvas: HTMLCanvasElement
  ) => {
    const videoAspect = videoWidth / videoHeight;
    const canvasAspect = canvas.width / canvas.height;

    let videoDisplayWidth: number, videoDisplayHeight: number, offsetX: number, offsetY: number;

    if (canvasAspect > videoAspect) {
      // Полосы сверху/снизу
      videoDisplayHeight = canvas.height;
      videoDisplayWidth = canvas.height * videoAspect;
      offsetX = (canvas.width - videoDisplayWidth) / 2;
      offsetY = 0;
    } else {
      // Полосы слева/справа
      videoDisplayWidth = canvas.width;
      videoDisplayHeight = canvas.width / videoAspect;
      offsetX = 0;
      offsetY = (canvas.height - videoDisplayHeight) / 2;
    }

    const scaleX = videoDisplayWidth / videoWidth;
    const scaleY = videoDisplayHeight / videoHeight;

    const x = offsetX + videoX * scaleX;
    const y = offsetY + videoY * scaleY;
    const w = videoW * scaleX;
    const h = videoH * scaleY;

    return { x, y, w, h };
  };

  // Инициализация iframe и canvas
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas || !camera.webrtc_out || !camera.source_id) return;

    let streamUrl = camera.webrtc_out;
    if (streamUrl.startsWith('https://') && streamUrl.includes('192.168.')) {
      streamUrl = streamUrl.replace('https://', 'http://');
    }

    // Создаем iframe
    const iframe = document.createElement('iframe');
    iframe.src = streamUrl;
    iframe.style.width = '100%';
    iframe.style.aspectRatio = '16 / 9';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';
    iframe.allow = 'autoplay; fullscreen';
    iframe.setAttribute('allowfullscreen', 'true');
    iframeRef.current = iframe;
    container.appendChild(iframe);

    // Обновление размера canvas
    const updateCanvasSize = () => {
      if (!iframe || !canvas) return;
      const rect = iframe.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      canvas.width = rect.width;
      canvas.height = rect.height;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
    };

    iframe.addEventListener('load', () => {
      setTimeout(updateCanvasSize, 100);
      setTimeout(updateCanvasSize, 500);
      setTimeout(updateCanvasSize, 1000);
    });

    window.addEventListener('resize', updateCanvasSize);

    // Обработчики для рисования
    const handleMouseDown = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      setRoiState(prev => ({
        ...prev,
        isDrawing: true,
        startX: e.clientX - rect.left,
        startY: e.clientY - rect.top,
        currentX: e.clientX - rect.left,
        currentY: e.clientY - rect.top,
      }));
    };

    const handleMouseMove = (e: MouseEvent) => {
      setRoiState(prev => {
        if (!prev.isDrawing) return prev;
        const rect = canvas.getBoundingClientRect();
        const newState = {
          ...prev,
          currentX: e.clientX - rect.left,
          currentY: e.clientY - rect.top,
        };
        // Перерисовываем после обновления состояния
        setTimeout(() => {
          const canvasEl = canvasRef.current;
          if (!canvasEl) return;
          const ctx = canvasEl.getContext('2d');
          if (!ctx) return;
          ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
          const x = Math.min(newState.startX, newState.currentX);
          const y = Math.min(newState.startY, newState.currentY);
          const w = Math.abs(newState.currentX - newState.startX);
          const h = Math.abs(newState.currentY - newState.startY);
          ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
          ctx.fillRect(0, 0, canvasEl.width, canvasEl.height);
          ctx.clearRect(x, y, w, h);
          ctx.strokeStyle = '#6366f1';
          ctx.lineWidth = 3;
          ctx.strokeRect(x, y, w, h);
        }, 0);
        return newState;
      });
    };

    const handleMouseUp = () => {
      setRoiState(prev => {
        if (!prev.isDrawing) return prev;
        const x = Math.min(prev.startX, prev.currentX);
        const y = Math.min(prev.startY, prev.currentY);
        const w = Math.abs(prev.currentX - prev.startX);
        const h = Math.abs(prev.currentY - prev.startY);

        if (w > 10 && h > 10) {
          return {
            ...prev,
            isDrawing: false,
            rect: { x, y, w, h },
          };
        } else {
          return {
            ...prev,
            isDrawing: false,
            rect: null,
          };
        }
      });
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
      if (iframeRef.current && iframeRef.current.parentNode) {
        iframeRef.current.parentNode.removeChild(iframeRef.current);
      }
      iframeRef.current = null;
    };
  }, [camera.webrtc_out, camera.source_id]);

  // При смене модели / типа зоны подтягиваем уже сохраненный ROI из status и рисуем его как оверлей
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) return;
    if (!camera.status || !camera.status.roi) return;

    const roiForModel = camera.status.roi[selectedModel];
    if (!roiForModel) {
      // Для этой модели нет сохраненного ROI
      setRoiState(prev => ({ ...prev, rect: null }));
      return;
    }

    // Используем только pre_crop как основную зону
    const srcRect: { x: number; y: number; w: number; h: number } | null = roiForModel.pre_crop || null;

    if (!srcRect) {
      setRoiState(prev => ({ ...prev, rect: null }));
      return;
    }

    const canvasRect = convertVideoToCanvasCoords(srcRect.x, srcRect.y, srcRect.w, srcRect.h, canvas);

    setRoiState(prev => ({
      ...prev,
      isDrawing: false,
      startX: canvasRect.x,
      startY: canvasRect.y,
      currentX: canvasRect.x + canvasRect.w,
      currentY: canvasRect.y + canvasRect.h,
      rect: {
        x: canvasRect.x,
        y: canvasRect.y,
        w: canvasRect.w,
        h: canvasRect.h,
      },
    }));
  }, [camera.status, selectedModel, videoWidth, videoHeight]);

  // Перерисовка ROI при изменении состояния
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (roiState.isDrawing || roiState.rect) {
      const x = roiState.isDrawing ? Math.min(roiState.startX, roiState.currentX) : roiState.rect!.x;
      const y = roiState.isDrawing ? Math.min(roiState.startY, roiState.currentY) : roiState.rect!.y;
      const w = roiState.isDrawing ? Math.abs(roiState.currentX - roiState.startX) : roiState.rect!.w;
      const h = roiState.isDrawing ? Math.abs(roiState.currentY - roiState.startY) : roiState.rect!.h;

      // Рисуем затемнение
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.clearRect(x, y, w, h);

      // Рисуем рамку
      ctx.strokeStyle = '#6366f1';
      ctx.lineWidth = 3;
      ctx.setLineDash([]);
      ctx.strokeRect(x, y, w, h);
    }
  }, [roiState]);

  const convertCanvasToVideoCoords = (canvasX: number, canvasY: number, canvasW: number, canvasH: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0, w: 0, h: 0 };

    const videoAspect = videoWidth / videoHeight;
    const canvasAspect = canvas.width / canvas.height;

    let videoDisplayWidth: number, videoDisplayHeight: number, offsetX: number, offsetY: number;

    if (canvasAspect > videoAspect) {
      videoDisplayHeight = canvas.height;
      videoDisplayWidth = canvas.height * videoAspect;
      offsetX = (canvas.width - videoDisplayWidth) / 2;
      offsetY = 0;
    } else {
      videoDisplayWidth = canvas.width;
      videoDisplayHeight = canvas.width / videoAspect;
      offsetX = 0;
      offsetY = (canvas.height - videoDisplayHeight) / 2;
    }

    const scaleX = videoWidth / videoDisplayWidth;
    const scaleY = videoHeight / videoDisplayHeight;

    const x = Math.round((canvasX - offsetX) * scaleX);
    const y = Math.round((canvasY - offsetY) * scaleY);
    const w = Math.round(canvasW * scaleX);
    const h = Math.round(canvasH * scaleY);

    const finalX = Math.max(0, Math.min(x, videoWidth));
    const finalY = Math.max(0, Math.min(y, videoHeight));
    const finalW = Math.max(0, Math.min(w, videoWidth - finalX));
    const finalH = Math.max(0, Math.min(h, videoHeight - finalY));

    return { x: finalX, y: finalY, w: finalW, h: finalH };
  };

  const handleSave = async () => {
    if (!roiState.rect || !camera.source_id || !selectedModel) return;

    setStatus({ type: 'loading', message: 'Отправка данных...' });

    try {
      const coords = convertCanvasToVideoCoords(
        roiState.rect.x,
        roiState.rect.y,
        roiState.rect.w,
        roiState.rect.h
      );

      // ROI
      await camerasAPI.setRoi(camera.source_id, selectedModel, 'pre_crop', coords);

      // Порог уверенности (если введен)
      const confValue = parseFloat(minConfidence.replace(',', '.'));
      if (!Number.isNaN(confValue) && confValue >= 0 && confValue <= 1) {
        await camerasAPI.setConfidence(camera.source_id, selectedModel, confValue);
      }

      // Кулдаун (если введен)
      const cooldownValue = parseInt(cooldown, 10);
      if (!Number.isNaN(cooldownValue) && cooldownValue >= 0) {
        await camerasAPI.setCooldown(camera.source_id, selectedModel, cooldownValue);
      }

      setStatus({ type: 'success', message: 'Настройки успешно сохранены' });
      setTimeout(() => {
        if (onUpdated) {
          onUpdated();
        }
        onClose();
      }, 800);
    } catch (error: any) {
      setStatus({ type: 'error', message: error.message || 'Ошибка при сохранении настроек' });
    }
  };

  const handleClear = async () => {
    if (!camera.source_id || !selectedModel) return;

    if (!window.confirm(`Вы уверены, что хотите сбросить зону ROI для модели «${getModelDisplayName(selectedModel)}»?`)) {
      return;
    }

    setStatus({ type: 'loading', message: 'Сброс зоны...' });

    try {
      // Сбрасываем только pre_crop для выбранной модели
      await camerasAPI.setRoi(camera.source_id, selectedModel, 'pre_crop', null);
      setStatus({ type: 'success', message: 'Зона ROI успешно сброшена' });
      setRoiState(prev => ({ ...prev, rect: null }));
      setTimeout(() => {
        if (onUpdated) {
          onUpdated();
        }
        onClose();
      }, 800);
    } catch (error: any) {
      setStatus({ type: 'error', message: error.message || 'Ошибка при сбросе зоны ROI' });
    }
  };

  const handleReset = () => {
    setRoiState(prev => ({ ...prev, rect: null, isDrawing: false }));
  };

  const coords = roiState.rect
    ? convertCanvasToVideoCoords(roiState.rect.x, roiState.rect.y, roiState.rect.w, roiState.rect.h)
    : null;

  return (
    <div className="roi-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="roi-modal-content">
        <div className="roi-modal-header">
          <h2>
            <img src={roiIconSvgUrl} alt="" className="roi-modal-title-icon" />
            Выбор зоны ROI
          </h2>
          <button className="roi-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="roi-controls">
          <div className="roi-control-group">
            <label htmlFor="roi-model">Модель:</label>
            {cameraModels.length > 0 ? (
              <select
                id="roi-model"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {cameraModels.map((model) => (
                  <option key={model} value={model}>
                    {getModelDisplayName(model)}
                  </option>
                ))}
              </select>
            ) : (
              <div className="roi-warning">
                ⚠️ К этой камере не привязано ни одной модели.<br />
                Сначала добавьте модель к камере, затем можно будет настроить зону ROI.
              </div>
            )}
          </div>

          <div className="roi-control-group">
            <label htmlFor="roi-confidence">Точность модели (0–1)</label>
            <input
              id="roi-confidence"
              type="number"
              min={0}
              max={1}
              step={0.05}
              className="roi-input"
              value={minConfidence}
              onChange={(e) => setMinConfidence(e.target.value)}
            />
          </div>

          <div className="roi-control-group">
            <label htmlFor="roi-cooldown">Задержка (секунды)</label>
            <input
              id="roi-cooldown"
              type="number"
              min={0}
              step={1}
              className="roi-input"
              value={cooldown}
              onChange={(e) => setCooldown(e.target.value)}
            />
          </div>

        </div>

        {camera.webrtc_out ? (
          <div className="roi-video-container" ref={containerRef}>
            <canvas ref={canvasRef} className="roi-canvas-overlay" />
          </div>
        ) : (
          <div className="roi-no-video">
            Видео поток недоступен для выбора зоны
          </div>
        )}

        {coords && (
          <div className="roi-info">
            <strong>Выбранная зона:</strong><br />
            X: {coords.x}, Y: {coords.y}, Ширина: {coords.w}, Высота: {coords.h}
          </div>
        )}

        {status.type && (
          <div className={`roi-status roi-status--${status.type}`}>
            {status.message}
          </div>
        )}

        <div className="roi-buttons">
          <button
            className="roi-btn roi-btn--primary"
            onClick={handleSave}
            disabled={!roiState.rect || !selectedModel || status.type === 'loading'}
            type="button"
          >
            <span className="roi-btn__inner">
              <img src={roiBtnIcon.save} alt="" className="roi-btn__icon" width={20} height={20} />
              Сохранить зону
            </span>
          </button>
          <button className="roi-btn roi-btn--secondary" onClick={handleReset} type="button">
            <span className="roi-btn__inner">
              <img src={roiBtnIcon.clear} alt="" className="roi-btn__icon" width={20} height={20} />
              Очистить выбор
            </span>
          </button>
          <button
            className="roi-btn roi-btn--secondary"
            onClick={handleClear}
            disabled={!selectedModel || status.type === 'loading'}
            type="button"
          >
            <span className="roi-btn__inner">
              <img src={roiBtnIcon.sbros} alt="" className="roi-btn__icon" width={20} height={20} />
              Сбросить зону
            </span>
          </button>
          <button className="roi-btn roi-btn--secondary" onClick={onClose} type="button">
            <span className="roi-btn__inner">
              <img src={roiBtnIcon.cancel} alt="" className="roi-btn__icon" width={18} height={18} />
              Отмена
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoiSelector;
